import React, { useState, useRef, useEffect } from 'react'
import './App.css'
import ChatMessage from './components/ChatMessage'
import ChatInput from './components/ChatInput'
import { Message } from './types'

// Declare window.ENV type
declare global {
  interface Window {
    ENV: {
      VITE_BACKEND_URL: string;
    };
  }
}

function App() {
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sessionEnded, setSessionEnded] = useState(false)
  const [trafficSource, setTrafficSource] = useState<string>('none')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Get backend URL from config (loaded in main.tsx)
  const BACKEND_URL = window.appConfig?.BACKEND_URL || 'http://localhost:5001'

  // Timer to ping backend when user is idle
  const idleTimerRef = useRef<number | null>(null);

  // fires when user is idle
  const triggerIdle = async () => {
    if (!sessionId || sessionEnded) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: '[idle timeout triggered]',
          session_id: sessionId,
          traffic_source: (window as any).trafficSource,
        }),
      });
      if (!res.ok) return;

      const data = await res.json();

      // Append server’s final assistant message (if any)
      if (data.response) {
        setMessages(prev => [...prev, {
          id: String(Date.now()),
          content: data.response,
          role: 'assistant',
          timestamp: new Date(),
        }]);
      }

      // End session on frontend
      if (data.session_ended) {
        setSessionEnded(true);
        if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      }
    } catch (e) {
      console.warn('[idle] ping failed', e);
    }
  };

  // reset the idle timer (30s for dev; change to 15*60*1000 for prod)
  const resetIdleTimer = () => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(triggerIdle, 16 * 60 * 1000);
  };

  // attach only after session exists; cleanup on unmount/changes
  useEffect(() => {
    if (!sessionId || sessionEnded) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'click']; // no 'scroll'
    const onActivity = () => resetIdleTimer();

    events.forEach((e) => document.addEventListener(e, onActivity, { passive: true } as any));
    resetIdleTimer();

    return () => {
      events.forEach((e) => document.removeEventListener(e, onActivity));
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    };
  }, [sessionId, sessionEnded, BACKEND_URL]);

  // Get traffic source from window object
  useEffect(() => {
    if (window.trafficSource) {
      setTrafficSource(window.trafficSource);
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Initialize chat with LLM prompt
  useEffect(() => {
    const initializeChat = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: "[new user has entered chat, begin]",
            session_id: sessionId,
            traffic_source: trafficSource // Send traffic source to backend
          })
        })

        if (!response.ok) {
          throw new Error('Failed to initialize chat')
        }

        const data = await response.json()

        // Store session ID for future requests
        if (data.session_id && !sessionId) {
          setSessionId(data.session_id)
        }

        const assistantMessage: Message = {
          id: Date.now().toString(),
          content: data.response || "Hey! There's an issue with the chat right now. Please try again later :)",
          role: 'assistant',
          timestamp: new Date()
        }

        setMessages([assistantMessage])
      } catch (error) {
        console.error('Error initializing chat:', error)

        const errorMessage: Message = {
          id: Date.now().toString(),
          content: "Hey! There's an issue with the chat right now. Please try again later :)",
          role: 'assistant',
          timestamp: new Date()
        }

        setMessages([errorMessage])
      } finally {
        setIsLoading(false)
      }
    }

    // Only initialize if we haven't already
    if (messages.length === 0 && isLoading) {
      initializeChat()
    }
  }, [sessionId, messages.length, isLoading, trafficSource])

  const sendMessage = async (content: string) => {
    if (!content.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      content: content.trim(),
      role: 'user',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)

    try {
      // Connect to backend API using the environment variable
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: content.trim(),
          session_id: sessionId,
          traffic_source: trafficSource // Send traffic source with each message
        })
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      const data = await response.json()

      // Store session ID for future requests
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id)
      }

      // Check if session has ended
      if (data.session_ended) {
        setSessionEnded(true)
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: data.response || "I'm sorry, I couldn't process your message. Please try again in a bit!",
        role: 'assistant',
        timestamp: new Date()
      }

      setMessages(prev => [...prev, assistantMessage])
    } catch (error) {
      console.error('Error sending message:', error)

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "I'm sorry, there was an error processing your message. Please try again in a bit!",
        role: 'assistant',
        timestamp: new Date()
      }

      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="app">
      <header className="app-header">
        <a
          href="https://www.wonderschool.com"
          target="_blank"
          rel="noopener noreferrer"
          className="logo-link"
        >
          <img src="/logo.svg" alt="Wonderschool" className="header-logo" />
        </a>
        <div className="header-buttons">
          <a
            href="https://meetings.hubspot.com/l-m/wonderschool?uuid=532a8352-4baa-447f-a9fa-21ff36ae4a48"
            target="_blank"
            rel="noopener noreferrer"
            className="header-button book-call"
          >
            Book a call
          </a>
          <a
            href="https://www.wonderschool.com/corp/new-providers-calculator-v3?"
            target="_blank"
            rel="noopener noreferrer"
            className="header-button income-calculator"
          >
            Income calculator
          </a>
        </div>
      </header>

      <main className="chat-container">
        <div className="messages">
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
          {isLoading && (
            <div className="loading-message">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          {sessionEnded && (
            <div className="session-ended-message">
              <p>✅ Conversation complete!</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      <footer className="chat-input-container">
        <ChatInput onSendMessage={sendMessage} disabled={isLoading || sessionEnded} />
      </footer>
    </div>
  )
}

export default App 